import React, { useCallback, useEffect, useState } from 'react';
import { UserButton, useUser } from '@clerk/clerk-react';
import { Link } from 'react-router-dom';
import { useApiAuth } from '../hooks/useApiAuth';
import SiteManager from '../components/admin/SiteManager';
import AboutManager from '../components/admin/AboutManager';
import ServicesManager from '../components/admin/ServicesManager';
import TestimonialsManager from '../components/admin/TestimonialsManager';
import PortfolioManager from '../components/admin/PortfolioManager';
import './Admin.css';

const TABS = [
  { key: 'site', label: 'Home page & site info' },
  { key: 'about', label: 'About page' },
  { key: 'services', label: 'Services page' },
  { key: 'portfolio', label: 'Portfolio' },
  { key: 'testimonials', label: 'Customer reviews' },
  { key: 'settings', label: 'Settings' },
];

export default function AdminDashboard() {
  const { user } = useUser();
  const { authFetch } = useApiAuth();
  const [tab, setTab] = useState('site');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [initializedAt, setInitializedAt] = useState(null); // null = still loading, false = never, string = ISO date

  const loadInitState = useCallback(async () => {
    try {
      const cfg = await authFetch('/api/admin/config');
      setInitializedAt(cfg?._initialized_at || false);
    } catch {
      // Fall back to public endpoint if the admin call errors; keeps the button
      // discoverable even when auth transiently hiccups.
      try {
        const pub = await fetch('/api/config').then((r) => r.json());
        setInitializedAt(pub?._initialized_at || false);
      } catch {
        setInitializedAt(false);
      }
    }
  }, [authFetch]);

  useEffect(() => { loadInitState(); }, [loadInitState]);

  const initDb = async () => {
    setBusy(true); setStatus('Initializing…');
    try {
      await authFetch('/api/admin/seed', { method: 'POST' });
      setStatus('Database ready.');
      await loadInitState();
    } catch (e) { setStatus(`Init error: ${e.message}`); }
    setBusy(false);
  };

  const initPending = initializedAt === false; // loaded, never initialised
  const initDone = typeof initializedAt === 'string';

  return (
    <main className="admin">
      <header className="admin-topbar">
        <div>
          <h1>Admin</h1>
          <p>Signed in as {user?.primaryEmailAddress?.emailAddress}</p>
        </div>
        <div className="admin-actions">
          <Link to="/" className="admin-link">← Back to site</Link>
          {initPending && (
            <button
              onClick={initDb}
              disabled={busy}
              className="admin-btn admin-btn-primary"
            >Initialize DB</button>
          )}
          <UserButton afterSignOutUrl="/" />
        </div>
      </header>

      {initPending && (
        <div className="admin-status admin-status-warn">
          The database hasn't been initialised yet. Click <strong>Initialize DB</strong> above to
          create the tables and copy the current site's content in.
        </div>
      )}

      <nav className="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`admin-tab ${tab === t.key ? 'is-active' : ''}`}
            onClick={() => { setTab(t.key); setStatus(''); }}
          >{t.label}</button>
        ))}
      </nav>

      {status && <div className="admin-status">{status}</div>}

      <section className="admin-panel">
        {tab === 'site' && <SiteManager setStatus={setStatus} />}
        {tab === 'about' && <AboutManager setStatus={setStatus} />}
        {tab === 'services' && <ServicesManager setStatus={setStatus} />}
        {tab === 'portfolio' && <PortfolioManager setStatus={setStatus} />}
        {tab === 'testimonials' && <TestimonialsManager setStatus={setStatus} />}
        {tab === 'settings' && (
          <div>
            <section className="admin-section">
              <h2>Account</h2>
              <p className="admin-muted">
                Signed in as <strong>{user?.primaryEmailAddress?.emailAddress}</strong>.
              </p>
            </section>

            <section className="admin-section">
              <h2>Database</h2>
              {initDone && (
                <p className="admin-muted">
                  Initialised {new Date(initializedAt).toLocaleString()}.
                </p>
              )}
              <p className="admin-muted" style={{ marginBottom: 12 }}>
                Re-running is safe: it only creates missing tables and only seeds defaults for
                empty tables. Your edits are never overwritten. Use this if a future update adds
                a new section that needs a table.
              </p>
              <button onClick={initDb} disabled={busy} className="admin-btn">
                Re-run Initialize DB
              </button>
            </section>
          </div>
        )}
      </section>
    </main>
  );
}
